import { useDebounce } from '@react-hook/debounce';
import classNames from 'classnames/bind';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useState } from 'react';
import Lottie from 'react-lottie-player';
import { v4 as uuidV4 } from 'uuid';

import LogoAnimation from '@/assets/animations/mint-logo-transparent-bg.json';
import { useLocale } from '@/helpers/LangHelper';
import { CategoryProps, StoreProps } from '@/types/Search';

import { OptionListNoResults } from './no-result';
import {
  CATEGORY_SUGGESTION_START_INDEX,
  SEARCH_PAGE_COR_ID_COOKIE_KEY,
  SEARCH_VERSION,
} from './search-bar.constants';
import styles from './search-bar.module.scss';
import { SuggestionHeading } from './suggestion-heading';
import { useAutocomplete } from './useAutocomplete';

const cx = classNames.bind(styles);

const highlightStores = (name: string, highlightString: string) => {
  const splitStoreName = name.split(highlightString);

  if (splitStoreName.length === 1) return name;

  return splitStoreName
    .flatMap((split, index) => {
      return [
        split,
        <span key={`${name}-${index}`} className={styles.storeNameHighlighted}>
          {highlightString}
        </span>,
      ];
    })
    .slice(0, -1);
};

interface SuggestionListProps {
  data: { name: string; id: string };
  searchUrlLink: string;
  highlightString?: string;
  activeSuggestedResult: number;
  index: number;
  searchTerm: string;
  httpCorrelationId: string;
  resultsCount: number;
}
const SuggestionList: React.FC<SuggestionListProps> = ({
  data,
  searchUrlLink,
  highlightString,
}) => {
  const suggestionName = highlightString ? highlightStores(data.name, highlightString) : data.name;

  return (
    <li className={styles.searchBarSuggestionOption} key={data.name}>
      <a href={searchUrlLink}>{suggestionName}</a>
    </li>
  );
};

const SuggestionMoreLink = ({ searchUrl, searchTerms }: any) => {
  return (
    <li className={styles.searchBarSuggestionSeeMoreLink}>
      <a
        href={`${searchUrl}?search_version=${SEARCH_VERSION.THREE}&q=${encodeURIComponent(
          searchTerms
        )}`}
      >
        See more results
      </a>
    </li>
  );
};

interface CategoriesOptionListProps {
  categories: CategoryProps[];
  searchUrl: string;
  activeSuggestedResult: number;
  searchTerm: string;
  httpCorrelationId: string;
}
const CategoriesOptionList: React.FC<CategoriesOptionListProps> = ({
  categories,
  searchUrl,
  activeSuggestedResult,
  searchTerm,
  httpCorrelationId,
}) => {
  const listCategoriesHeadingText = 'Categories',
    listNoCategoriesText = 'No categories found';

  const categoriesSearchResults =
    categories.length > 0 ? (
      categories.map((category, index) => (
        <SuggestionList
          key={category.id}
          data={category}
          searchUrlLink={`${searchUrl}/${category.slug}`}
          searchTerm={searchTerm}
          activeSuggestedResult={activeSuggestedResult}
          index={index + CATEGORY_SUGGESTION_START_INDEX}
          httpCorrelationId={httpCorrelationId}
          resultsCount={categories.length}
        />
      ))
    ) : (
      <OptionListNoResults noResultsText={listNoCategoriesText} />
    );

  return (
    <>
      <SuggestionHeading text={listCategoriesHeadingText} />
      {categoriesSearchResults}
    </>
  );
};

interface StoresOptionListProps {
  stores: StoreProps[];
  searchUrl: string;
  highlightString: string;
  activeSuggestedResult: number;
  searchTerm: string;
  httpCorrelationId: string;
}
const StoresOptionList: React.FC<StoresOptionListProps> = ({
  stores,
  searchUrl,
  highlightString,
  activeSuggestedResult,
  searchTerm,
  httpCorrelationId,
}) => {
  const listStoresHeadingText = 'Stores',
    listNoStoresText = 'No stores found';

  const storesSearchResults =
    stores.length > 0 ? (
      stores.slice(0, 8).map((store, index) => {
        return (
          <SuggestionList
            key={store.id}
            data={store}
            searchUrlLink={`${searchUrl}?search_version=${
              SEARCH_VERSION.THREE
            }&q=${encodeURIComponent(store.name)}`}
            highlightString={highlightString}
            activeSuggestedResult={activeSuggestedResult}
            index={index}
            httpCorrelationId={httpCorrelationId}
            resultsCount={stores.length}
            searchTerm={searchTerm}
          />
        );
      })
    ) : (
      <OptionListNoResults noResultsText={listNoStoresText} />
    );

  return (
    <>
      <SuggestionHeading text={listStoresHeadingText} />
      {storesSearchResults}
      {stores.length > 8 && (
        <SuggestionMoreLink searchUrl={searchUrl} searchTerms={highlightString} />
      )}
    </>
  );
};

const SearchBar: React.FC = () => {
  const router = useRouter();
  const { locale } = useLocale();
  //   const locale = 'en-US';

  const [{ searchTerm, httpCorrelationId }, setSearchData] = useDebounce(
    { searchTerm: '', httpCorrelationId: uuidV4() },
    100
  );

  const [suggestedStores, setSuggestedStores] = useState<StoreProps[]>([]);
  const [showSuggestionList, setShowSuggestionList] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState<CategoryProps[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [activeSuggestedResult, setActiveSuggestedResult] = useState(-1);

  const storeSearchUrl = `/${locale}/stores/search`;
  const categorySearchUrl = `/${locale}/categories`;

  const autocompleteData = useAutocomplete(
    searchTerm,
    httpCorrelationId,
    locale,
    SEARCH_VERSION.THREE
  );

  useEffect(() => {
    Cookies.set(SEARCH_PAGE_COR_ID_COOKIE_KEY, httpCorrelationId);
  }, [httpCorrelationId]);

  useEffect(() => {
    if (autocompleteData) {
      setSuggestedStores(autocompleteData.stores);
      setSuggestedCategories(autocompleteData.categories);
      setIsLoadingSuggestions(false);
    }
  }, [autocompleteData]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.currentTarget.value;
    setSearchData({ searchTerm: newSearchTerm, httpCorrelationId: uuidV4() });
    setShowSuggestionList(!!newSearchTerm);

    if (newSearchTerm) {
      setIsLoadingSuggestions(true);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter': {
        if (activeSuggestedResult >= CATEGORY_SUGGESTION_START_INDEX) {
          const highlightedIndex = activeSuggestedResult - CATEGORY_SUGGESTION_START_INDEX;
          return router.push(`/categories/${suggestedCategories[highlightedIndex].slug}`);
        }

        const searchQuery = suggestedStores[activeSuggestedResult]
          ? suggestedStores[activeSuggestedResult].name
          : e.currentTarget.value;

        return router.push(
          `/stores/search?search_version=${SEARCH_VERSION.THREE}&q=${encodeURIComponent(
            searchQuery
          )}`
        );
      }
      case 'ArrowUp': {
        if (activeSuggestedResult === 0) {
          return;
        }
        return setActiveSuggestedResult(activeSuggestedResult - 1);
      }
      case 'ArrowDown': {
        if (activeSuggestedResult - 1 === suggestedStores.length) {
          return;
        }
        return setActiveSuggestedResult(activeSuggestedResult + 1);
      }
    }
  };

  const suggestionListClassName = cx({
    [styles.searchBarSuggestionWrapper]: true,
    [styles.showSuggestionList]: showSuggestionList,
  });

  const suggestionsListContent = isLoadingSuggestions ? (
    <div className={styles.loadingSpinnerContainer}>
      <Lottie
        animationData={LogoAnimation}
        rendererSettings={{
          preserveAspectRatio: 'xMidYMid slice',
        }}
        loop
        play
      />
    </div>
  ) : (
    <>
      <StoresOptionList
        stores={suggestedStores}
        searchUrl={storeSearchUrl}
        highlightString={searchTerm}
        activeSuggestedResult={activeSuggestedResult}
        httpCorrelationId={httpCorrelationId}
        searchTerm={searchTerm}
      />
      <div className={styles.optionListDivider} />
      <CategoriesOptionList
        categories={suggestedCategories}
        searchUrl={categorySearchUrl}
        activeSuggestedResult={activeSuggestedResult}
        httpCorrelationId={httpCorrelationId}
        searchTerm={searchTerm}
      />
    </>
  );

  return (
    <div
      className={styles.searchBarWrapper}
      onFocus={() => {
        setShowSuggestionList(!!searchTerm);
      }}
      onBlur={(e: FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setShowSuggestionList(false);
        }
      }}
    >
      <input
        className={styles.searchInput}
        placeholder="Search brands, products and stores"
        type="text"
        onChange={onChange}
        onKeyDown={onKeyDown}
      ></input>
      <ul data-testid="search-suggestions" className={suggestionListClassName}>
        {suggestionsListContent}
      </ul>
    </div>
  );
};

export const SearchBarV3 = SearchBar;
